import get from 'lodash.get';
import { isCategory } from '../../utils/categoryHelpers';

export const FILTER_STATS_PRICE = 'price';
export const FILTER_STATS_PRICE_TRADE = 'pricetrade';
export const FILTER_STATS_WIDTH = 'width';
export const FILTER_STATS_HEIGHT = 'height';
export const FILTER_STATS_DEPTH = 'depth';
export const FILTER_STATS_LENGTH = 'length';

export const FILTERS_STATS = [
    FILTER_STATS_PRICE,
    FILTER_STATS_PRICE_TRADE,
    FILTER_STATS_WIDTH,
    FILTER_STATS_HEIGHT,
    FILTER_STATS_DEPTH,
    FILTER_STATS_LENGTH,
];

export const DIMENSION_STATS = [
    FILTER_STATS_WIDTH,
    FILTER_STATS_HEIGHT,
    FILTER_STATS_DEPTH,
    FILTER_STATS_LENGTH,
];

const FILTER_VALUE_WILDCARD = '*';

function isFilter(filterTypes = [], filterName = '') {
    return filterTypes.map(s => s.toLowerCase()).includes(filterName.toLowerCase());
}

export function getFilterValues(filters = [], filterName) {
    const appliedFilter = filters.find(o => o.name === filterName) || { values: [] };
    return appliedFilter.values;
}

export function getAppliedFilter(filters = [], filterName) {
    return filters.find(o => o.name === filterName) || null;
}

export const isStatsFilter = isFilter.bind(null, FILTERS_STATS);

export const isPriceFilter = isFilter.bind(null, [FILTER_STATS_PRICE, FILTER_STATS_PRICE_TRADE]);

export const isDimensionFilter = isFilter.bind(null, DIMENSION_STATS);

/**
 * Min and max have to always be rounded in the same way, for everything except the carat weight filter
 */
export const roundMinMax = (min, max, numeric = false) => {
    if (min !== FILTER_VALUE_WILDCARD) {
        min = min < 1 ? Math.ceil(min || 1) : Math.floor(min);
    }
    max = max === FILTER_VALUE_WILDCARD ? max : Math.ceil(max);
    if (numeric) {
        return [min, max];
    } else {
        return [min, max].map(n => n.toString());
    }
};

/**
 * Min and max have to be rounded to 1 symbol after the comma for carat weight
 */
export function roundCaratWeightRange(min, max) {
    // Controls the count of numbers after the trailing comma. Index of 100 is for two numbers
    const precision = 100;

    min =
        min === FILTER_VALUE_WILDCARD || min < 0
            ? Math.floor((min || 0) * precision) / precision
            : Math.floor(min * precision) / precision;
    max = Math.floor(max * precision) / precision;

    return [min, max];
}

/**
 * Expect a valid range string, `[1 TO 6]` or [1.1 TO 2.2]
 */
export function parseRangeDisplayValue(rangeString) {
    const rangeMatch = rangeString.match(/(?:\.?\d)+|\*/g) || [];
    const min = rangeMatch[0] || FILTER_VALUE_WILDCARD;
    const max = rangeMatch[1] || FILTER_VALUE_WILDCARD;
    return [min, max];
}

function handleWildcards(appliedRange, availableRange) {
    return appliedRange
        .map((value, index) => {
            if (!value || value === FILTER_VALUE_WILDCARD) {
                return availableRange[index];
            } else {
                return value;
            }
        })
        .map(parseFloat);
}

/**
 * Given an applied filter value and a secondary available filter value, determines which displayName should be shown
 * to the user.
 */
export function constructStatsRange(appliedDisplayValue, availableDisplayValue) {
    const [appliedRange, availableRange] = [appliedDisplayValue, availableDisplayValue]
        .map(filterValue => filterValue || '')
        .map(parseRangeDisplayValue);

    const [min, max] = handleWildcards(appliedRange, availableRange);

    return roundMinMax(min, max, true);
}

/**
 * Given an applied filter value and a secondary available filter value, determines which displayName should be shown
 * to the user for the carat weight filter.
 */
export function constructCaratWeightStatsRange(appliedDisplayValue, availableDisplayValue) {
    const [appliedRange, availableRange] = [appliedDisplayValue, availableDisplayValue]
        .map(filterValue => filterValue || '')
        .map(parseRangeDisplayValue);

    const [min, max] = handleWildcards(appliedRange, availableRange);

    return roundCaratWeightRange(min, max);
}

export function formatRange([min, max]) {
    const [minValue, maxValue] = [min, max].map(value => {
        if (value !== 0 && !value) {
            return FILTER_VALUE_WILDCARD;
        } else {
            return value;
        }
    });
    return {
        urlLabel: `[${minValue} TO ${maxValue}]`,
        displayName: `${minValue},${maxValue}`,
    };
}

export function getInputRangeMinMax({
    filter = {},
    filterName = '',
    statKey = '',
    appliedFilters = [],
}) {
    const appliedFilterValues = getFilterValues(appliedFilters, filterName);

    const stats = get(filter, 'values[0].stats') || [];
    const availableStat = stats.find(stat => stat.key === statKey) || {};

    const availableDisplayValue = availableStat.values || '';
    const appliedDisplayValue = get(appliedFilterValues[0], 'urlLabel') || '';

    const [min, max] = constructStatsRange(appliedDisplayValue, availableDisplayValue);
    const [parsedMin, parsedMax] = parseRangeDisplayValue(availableDisplayValue);
    const [availableMin, availableMax] = roundMinMax(parsedMin, parsedMax);

    return { min, max, availableMin, availableMax };
}

export const removeUnusableFilters = (filters = [], currentCategory) => {
    return filters.filter(filter => {
        const { name } = filter;
        // if currentCategory equals or higher than available filter - this means that we reach the end or last category
        // so we can get rid off unused categories
        const isUnusedCategoryFilter = isCategory(filter) && name <= currentCategory;
        return !isUnusedCategoryFilter;
    });
};

/**
 * Returns specific value for specific filter name determined by `urlLabel`
 */
export function getFilterValueByUrlLabel(
    appliedFilters,
    expectedFilterName,
    expectedFilterValueUrlLabel
) {
    return getFilterValues(appliedFilters, expectedFilterName).find(
        value => value && value.urlLabel === expectedFilterValueUrlLabel
    );
}
